// A PAGE THAT OUTLIVED THE BUILD IT CAME FROM — detect it, and reload once.
//
// ⚠️ THE BUG THIS EXISTS FOR, reproduced on production code: deploy while
// somebody's sign-in page is open, then let them click. Next encrypts server
// action arguments with a key generated PER BUILD, so the old page's payload is
// undecryptable by the new server, and the click dies with
//
//     DOMException [OperationError]: The operation failed
//       [cause]: [Error: Cipher job failed]
//
// on the server and "Application error" on screen. A sign-in spans minutes —
// load the page, go to Slack, come back, click — which is exactly wide enough
// for a deploy to land inside it. The same window breaks any stale page whose
// chunks the new build no longer has.
//
// ⚠️ IT COMPARES BUILD IDS RATHER THAN READING THE ERROR, and that is the whole
// design. In production React redacts the message ("The specific message is
// omitted in production builds…") and leaves a digest, so a stale-build failure
// is textually identical to every other server error. Matching on text would
// mean either missing this one or reloading on all of them — and reloading on a
// genuine bug hides it behind a refresh loop-shaped symptom. The build id is the
// only signal that says "the server has moved on" and nothing else.
//
// ⚠️ AND IT RELOADS AT MOST ONCE PER BUILD. The guard is keyed on the build we
// reloaded TO, in sessionStorage, so a bug that survives the reload shows the
// error boundary the second time instead of refreshing forever. A reload loop
// is worse than the error it is papering over: it is unreadable, unreportable,
// and it eats the page before anyone can photograph it.

/** Where the current build id is published for the client to read. */
export const BUILD_META = "x-ironside-build";
const ENDPOINT = "/api/build-id";
const GUARD = "ironside:stale-build-reload";

/** The build this DOCUMENT was rendered by, or null if the tag is missing. */
export function pageBuildId(): string | null {
  if (typeof document === "undefined") return null;
  return document.querySelector<HTMLMetaElement>(`meta[name="${BUILD_META}"]`)?.content ?? null;
}

/**
 * Has the server moved on since this page was served? Returns the server's
 * build id when it differs, null when it matches or cannot be determined.
 *
 * A failed fetch answers null, not "stale": the reason we are here is that
 * something already went wrong, and a network blip must not become a reload.
 */
export async function serverMovedOn(fetchImpl: typeof fetch = fetch): Promise<string | null> {
  try {
    const mine = pageBuildId();
    if (!mine || mine === "development") return null;

    const res = await fetchImpl(ENDPOINT, { cache: "no-store" });
    if (!res.ok) return null;

    const { buildId } = (await res.json()) as { buildId?: unknown };
    if (typeof buildId !== "string" || !buildId) return null;

    return buildId === mine ? null : buildId;
  } catch {
    return null;
  }
}

/**
 * If this page is from a build the server has replaced, reload it — once.
 *
 * Returns true when a reload was started, so a caller can stop rendering an
 * error the user is about to navigate away from anyway.
 *
 * `location.replace(href)` rather than `reload()`: a reload can re-submit the
 * navigation that failed, and this must be a plain GET of the same URL.
 */
export async function reloadIfStale(deps: {
  fetchImpl?: typeof fetch;
  storage?: Pick<Storage, "getItem" | "setItem">;
  location?: { href: string; replace: (url: string) => void };
} = {}): Promise<boolean> {
  const loc = deps.location ?? (typeof location === "undefined" ? undefined : location);
  if (!loc) return false;

  const server = await serverMovedOn(deps.fetchImpl ?? fetch);
  if (!server) return false;

  return reloadOnceTo(server, { storage: deps.storage, location: loc });
}

/**
 * May this tab load a document to land on `server`'s build? Records that it
 * did, so the answer for the same build is no from then on.
 *
 * The guard both callers share: the error boundary's reloadIfStale above, and
 * a stale tab's next navigation (lib/stale-tab.ts), which learns the
 * server's build from the store poll's header instead of asking for it. One key, so a tab that one of them loaded to build B is not
 * loaded to B again by the other: a page that still disagrees with the server
 * after a document load (a cached document, a half-finished deploy) has had
 * its one try. A later deploy, or a rollback, is a different build and earns a
 * fresh one: the key holds only the last build loaded to.
 *
 * Storage that throws (private browsing) answers yes, unrecorded. Neither
 * caller can loop on it: the boundary runs only on an error, and after one
 * load the ids agree; a navigation is one click, never a timer.
 */
export function takeReloadGuard(server: string, storage?: Pick<Storage, "getItem" | "setItem">): boolean {
  const store = storage ?? (typeof sessionStorage === "undefined" ? undefined : sessionStorage);
  try {
    if (store?.getItem(GUARD) === server) return false;
    store?.setItem(GUARD, server);
  } catch {
    // One unguarded load still beats a dead page (see above).
  }
  return true;
}

/**
 * Reload this page to land on `server`'s build, unless this tab already did
 * (takeReloadGuard). `location.replace(href)`: see reloadIfStale.
 */
export function reloadOnceTo(
  server: string,
  deps: {
    storage?: Pick<Storage, "getItem" | "setItem">;
    location?: { href: string; replace: (url: string) => void };
  } = {},
): boolean {
  const loc = deps.location ?? (typeof location === "undefined" ? undefined : location);
  if (!loc) return false;
  if (!takeReloadGuard(server, deps.storage)) return false;
  loc.replace(loc.href);
  return true;
}
